import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { rotuloStatus, corStatus, rotuloTipo, STATUS_LISTA } from '../lib/rotulos';

interface OS {
  id: number;
  codigo: string;
  tipo_os: string;
  status: string;
  data_agendada?: string | null;
  hora_agendada?: string | null;
  ativo_id?: number | null;
  ativo_nome?: string | null;
  ativo_patrimonio?: string | null;
  unidade_nome: string | null;
  local_nome: string | null;
  tecnico_nome: string | null;
  cliente_nome?: string | null;
  veiculo_placa?: string | null;
  veiculo_marca?: string | null;
  veiculo_modelo?: string | null;
  veiculo_ano?: number | null;
  // Processo por etapas: progresso do usuário logado nesta OS.
  etapas_minhas_total?: number;
  etapas_minhas_concluidas?: number;
  etapas_minha_parte_concluida?: boolean;
}

interface TecnicoFiltro {
  id: number;
  nome: string;
  status?: string;
}

interface AtivoFiltro {
  id: number;
  nome: string;
  patrimonio?: string | null;
  status?: string;
}

interface ClienteFiltro {
  id: number;
  nome: string;
  status?: string;
}

interface RespostaOSLista {
  linhas: OS[];
  paginacao: {
    pagina: number;
    limite: number;
    total_registros: number;
    total_paginas: number;
  };
}

interface FiltrosOS {
  status: string;
  numero_os: string;
  tecnico_id: string;
  data_inicio: string;
  data_fim: string;
  patrimonio: string;
  ativo_id: string;
  cliente_id: string;
}

const LIMITE_POR_PAGINA = 10;

function veiculoLabel(os: OS): string {
  return [os.veiculo_placa, os.veiculo_marca, os.veiculo_modelo, os.veiculo_ano].filter(Boolean).join(' ');
}

function agendamentoLabel(os: OS): string {
  if (!os.data_agendada) return '-';
  const partes = String(os.data_agendada).split('-');
  const dataFmt = partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : String(os.data_agendada);
  const hora = os.hora_agendada ? String(os.hora_agendada).slice(0, 5) : '';
  return hora ? `${dataFmt} ${hora}` : dataFmt;
}

function filtrosIniciais(): FiltrosOS {
  return {
    status: '',
    numero_os: '',
    tecnico_id: '',
    data_inicio: '',
    data_fim: '',
    patrimonio: '',
    ativo_id: '',
    cliente_id: '',
  };
}

// Com o processo por etapas ativo, quem já concluiu as suas etapas não pode ver
// a OS como "Em execução": mostramos a situação DELE, e a OS segue aberta para
// o gestor fechar.
function badgeStatusOS(os: OS) {
  const encerrada = os.status === 'concluido' || os.status === 'cancelado';
  if (os.etapas_minha_parte_concluida && !encerrada) {
    return {
      texto: 'Suas etapas concluídas',
      cor: 'var(--ok)',
      titulo: 'Você concluiu as suas etapas. A OS segue em execução até o gestor fechá-la.',
    };
  }
  return { texto: rotuloStatus(os.status), cor: corStatus(os.status), titulo: '' };
}

export default function OSLista() {
  const { user, isPremium } = useAuth();
  const [dados, setDados] = useState<RespostaOSLista | null>(null);
  const [tecnicos, setTecnicos] = useState<TecnicoFiltro[]>([]);
  const [clientes, setClientes] = useState<ClienteFiltro[]>([]);
  const [equipamentos, setEquipamentos] = useState<AtivoFiltro[]>([]);
  // Busca que apenas ENCURTA a lista do select. Nao entra na consulta.
  const [buscaEquipamento, setBuscaEquipamento] = useState('');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState<FiltrosOS>(filtrosIniciais);
  const [filtrosAplicados, setFiltrosAplicados] = useState<FiltrosOS>(filtrosIniciais);
  const modoOficina = user?.modo_visualizacao === 'oficina';
  const podeFiltrarPorPatrimonio = !modoOficina && Boolean(user?.filtro_patrimonio_habilitado);
  const podeCriarOs = !(user?.perfil === 'tecnico' && user?.tecnico_nao_pode_criar_os);
  const lista = dados?.linhas || [];
  const totalPaginas = dados?.paginacao.total_paginas ?? 1;
  const totalRegistros = dados?.paginacao.total_registros ?? 0;

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const qs = new URLSearchParams();
      qs.set('paginado', '1');
      qs.set('pagina', String(pagina));
      qs.set('limite', String(LIMITE_POR_PAGINA));
      if (filtrosAplicados.status) qs.set('status', filtrosAplicados.status);
      if (filtrosAplicados.numero_os) qs.set('numero_os', filtrosAplicados.numero_os);
      if (filtrosAplicados.tecnico_id) qs.set('tecnico_id', filtrosAplicados.tecnico_id);
      if (filtrosAplicados.data_inicio) qs.set('data_inicio', filtrosAplicados.data_inicio);
      if (filtrosAplicados.data_fim) qs.set('data_fim', filtrosAplicados.data_fim);
      if (podeFiltrarPorPatrimonio && filtrosAplicados.patrimonio) qs.set('patrimonio', filtrosAplicados.patrimonio);
      if (!modoOficina && filtrosAplicados.ativo_id) qs.set('ativo_id', filtrosAplicados.ativo_id);
      if (filtrosAplicados.cliente_id) qs.set('cliente_id', filtrosAplicados.cliente_id);
      setDados(await api<RespostaOSLista>('/os/os_listar.php?' + qs.toString()));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }, [pagina, filtrosAplicados, podeFiltrarPorPatrimonio, modoOficina]);

  async function carregarTecnicos() {
    try {
      const listaTecnicos = await api<TecnicoFiltro[]>('/cadastros/tecnicos.php');
      setTecnicos(listaTecnicos.filter((item) => item.status !== 'inativo'));
    } catch {
      setTecnicos([]);
    }
  }

  async function carregarClientes() {
    try {
      const listaClientes = await api<ClienteFiltro[]>('/cadastros/clientes.php?somente_ativos=1');
      setClientes(listaClientes.filter((item) => item.status !== 'inativo'));
    } catch {
      setClientes([]);
    }
  }

  useEffect(() => {
    carregarTecnicos();
    carregarClientes();
  }, []);

  useEffect(() => {
    if (modoOficina || !isPremium) {
      setEquipamentos([]);
      return;
    }
    api<AtivoFiltro[]>('/ativos/ativos.php')
      .then((lista) => setEquipamentos(lista.filter((a) => a.status !== 'inativo')))
      .catch(() => setEquipamentos([]));
  }, [modoOficina, isPremium]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function aplicarFiltros(e: FormEvent) {
    e.preventDefault();
    setPagina(1);
    setFiltrosAplicados({
      status: filtros.status,
      numero_os: filtros.numero_os.trim(),
      tecnico_id: filtros.tecnico_id,
      data_inicio: filtros.data_inicio,
      data_fim: filtros.data_fim,
      patrimonio: filtros.patrimonio.trim(),
      ativo_id: filtros.ativo_id,
      cliente_id: filtros.cliente_id,
    });
  }

  function limparFiltros() {
    const limpo = filtrosIniciais();
    setBuscaEquipamento('');
    setFiltros(limpo);
    setFiltrosAplicados(limpo);
    setPagina(1);
  }

  // Uma opcao por EQUIPAMENTO: o rotulo leva o patrimonio para dois equipamentos
  // de mesmo nome nao ficarem indistinguiveis na lista.
  const opcoesEquipamento = useMemo(() => equipamentos
    .map((a) => ({
      id: String(a.id),
      rotulo: [(a.nome || '').trim(), (a.patrimonio || '').trim()].filter(Boolean).join(' \u00b7 '),
    }))
    .filter((o) => o.rotulo)
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')), [equipamentos]);

  const termoEquip = buscaEquipamento.trim().toLowerCase();
  // A opcao escolhida fica na lista mesmo que a busca a exclua: sem isso o
  // <select> ficaria com um value que nao existe entre as options e perderia a
  // selecao sem avisar.
  const opcoesVisiveis = termoEquip
    ? opcoesEquipamento.filter((o) => o.rotulo.toLowerCase().includes(termoEquip) || o.id === filtros.ativo_id)
    : opcoesEquipamento;

  const equipamentosFiltrados = podeFiltrarPorPatrimonio && filtrosAplicados.patrimonio
    ? lista.reduce<Array<{ id: number | string; nome: string; patrimonio: string }>>((acc, os) => {
      const id = os.ativo_id ?? os.ativo_nome ?? '';
      const nome = (os.ativo_nome || '').trim();
      const patrimonioLinha = (os.ativo_patrimonio || '').trim();
      if (!id || !nome || !patrimonioLinha || acc.some((item) => item.id === id)) return acc;
      acc.push({ id, nome, patrimonio: patrimonioLinha });
      return acc;
    }, [])
    : [];

  return (
    <div>
      <div className="titulo-pg">
        <h1>Ordens de serviço</h1>
        {modoOficina
          ? <Link to="/app/orcamentos" className="btn">Criar chamado</Link>
          : podeCriarOs && <Link to="/app/os/nova" className="btn">+ Nova OS</Link>}
      </div>

      <form className="card" onSubmit={aplicarFiltros} style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Status</label>
            <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}>
              <option value="">Todos os status</option>
              {STATUS_LISTA.map((s) => <option key={s} value={s}>{rotuloStatus(s)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Número da OS</label>
            <input
              className="input"
              value={filtros.numero_os}
              onChange={(e) => setFiltros({ ...filtros, numero_os: e.target.value })}
              placeholder="Ex.: OS-001 ou 1"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Técnico</label>
            <select value={filtros.tecnico_id} onChange={(e) => setFiltros({ ...filtros, tecnico_id: e.target.value })}>
              <option value="">Todos os técnicos</option>
              {tecnicos.map((tecnico) => (
                <option key={tecnico.id} value={tecnico.id}>{tecnico.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Data início</label>
            <input
              className="input"
              type="date"
              value={filtros.data_inicio}
              onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Data fim</label>
            <input
              className="input"
              type="date"
              value={filtros.data_fim}
              onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
            />
          </div>
          {!modoOficina && (
            <div>
              <label style={{ display: 'block', marginBottom: 6 }}>Equipamento</label>
              {opcoesEquipamento.length > 8 && (
                <input
                  className="input"
                  style={{ marginBottom: 6 }}
                  value={buscaEquipamento}
                  onChange={(e) => setBuscaEquipamento(e.target.value)}
                  placeholder={`Buscar na lista (${opcoesEquipamento.length})`}
                />
              )}
              <select
                value={filtros.ativo_id}
                onChange={(e) => setFiltros({ ...filtros, ativo_id: e.target.value })}
              >
                <option value="">Todos os equipamentos</option>
                {opcoesVisiveis.map((o) => <option key={o.id} value={o.id}>{o.rotulo}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Cliente</label>
            <select value={filtros.cliente_id} onChange={(e) => setFiltros({ ...filtros, cliente_id: e.target.value })}>
              <option value="">Todos os clientes</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>{cliente.nome}</option>
              ))}
            </select>
          </div>
          {podeFiltrarPorPatrimonio && (
            <div>
              <label style={{ display: 'block', marginBottom: 6 }}>Patrimônio</label>
              <input
                className="input"
                value={filtros.patrimonio}
                onChange={(e) => setFiltros({ ...filtros, patrimonio: e.target.value })}
                placeholder="Digite o patrimônio"
              />
            </div>
          )}
        </div>
        <div className="form-actions" style={{ marginTop: 14 }}>
          <button className="btn sm" type="submit">Filtrar</button>
          <button className="btn sec sm" type="button" onClick={limparFiltros}>Limpar</button>
        </div>
      </form>

      {erro && <div className="msg-erro">{erro}</div>}
      {podeFiltrarPorPatrimonio && filtrosAplicados.patrimonio && (
        <div className="card" style={{ marginBottom: 12 }}>
          <strong>Patrimônio pesquisado:</strong> {filtrosAplicados.patrimonio}
          <div style={{ marginTop: 8, color: 'var(--texto-suave)' }}>
            {equipamentosFiltrados.length
              ? equipamentosFiltrados.map((ativo) => `${ativo.nome} (${ativo.patrimonio})`).join(' | ')
              : 'Nenhum equipamento encontrado para este patrimônio na página atual.'}
          </div>
        </div>
      )}

      <div className="tabela-wrap">
        {modoOficina ? (
          <table>
            <thead><tr><th>Código</th><th>Veículo</th><th>Cliente</th><th>Serviço</th><th>Técnico</th><th>Agendamento</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {lista.map((os) => (
                <tr key={os.id}>
                  <td><strong>{os.codigo}</strong></td>
                  <td>{veiculoLabel(os) || '-'}</td>
                  <td>{os.cliente_nome || '-'}</td>
                  <td>{os.tipo_os || '-'}</td>
                  <td>{os.tecnico_nome || '-'}</td>
                  <td>{agendamentoLabel(os)}</td>
                  <td>
                    {(() => {
                      const b = badgeStatusOS(os);
                      return (
                        <span className="badge" title={b.titulo} style={{ background: 'transparent', color: b.cor, border: `1px solid ${b.cor}` }}>
                          {b.texto}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'right' }}><Link to={'/app/os/' + os.id} className="btn sec sm">Abrir</Link></td>
                </tr>
              ))}
              {!lista.length && <tr><td colSpan={8} className="vazio">Nenhuma OS encontrada.</td></tr>}
            </tbody>
          </table>
        ) : (
          <table>
            <thead><tr><th>Código</th><th>Tipo</th>{podeFiltrarPorPatrimonio && <th>Equipamento</th>}<th>Local</th><th>Cliente</th><th>Técnico</th><th>Agendamento</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {lista.map((os) => (
                <tr key={os.id}>
                  <td><strong>{os.codigo}</strong></td>
                  <td>{rotuloTipo(os.tipo_os)}</td>
                  {podeFiltrarPorPatrimonio && (
                    <td>
                      <strong>{os.ativo_nome || '-'}</strong>
                      <br />
                      <small>{os.ativo_patrimonio || '-'}</small>
                    </td>
                  )}
                  <td>{[os.unidade_nome, os.local_nome].filter(Boolean).join(' / ') || '-'}</td>
                  <td>{os.cliente_nome || '-'}</td>
                  <td>{os.tecnico_nome || '-'}</td>
                  <td>{agendamentoLabel(os)}</td>
                  <td>
                    {(() => {
                      const b = badgeStatusOS(os);
                      return (
                        <span className="badge" title={b.titulo} style={{ background: 'transparent', color: b.cor, border: `1px solid ${b.cor}` }}>
                          {b.texto}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'right' }}><Link to={'/app/os/' + os.id} className="btn sec sm">Abrir</Link></td>
                </tr>
              ))}
              {!lista.length && <tr><td colSpan={podeFiltrarPorPatrimonio ? 9 : 8} className="vazio">Nenhuma OS encontrada.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <div className="paginacao-lista">
        <span>
          Exibindo {lista.length} de {totalRegistros} OS. Página {dados?.paginacao.pagina ?? 1} de {totalPaginas}.
        </span>
        <div className="paginacao-acoes">
          <button className="btn sec sm" disabled={pagina <= 1} onClick={() => setPagina(1)}>Primeira</button>
          <button className="btn sec sm" disabled={pagina <= 1} onClick={() => setPagina((atual) => Math.max(1, atual - 1))}>Anterior</button>
          <button className="btn sec sm" disabled={pagina >= totalPaginas} onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))}>Próxima</button>
          <button className="btn sec sm" disabled={pagina >= totalPaginas} onClick={() => setPagina(totalPaginas)}>Última</button>
        </div>
      </div>
    </div>
  );
}
